// src/Hooks/useFilterManage.js

import { useState, useEffect } from 'react';
import { collection, onSnapshot, getDocs, query } from 'firebase/firestore';
import { db } from '../Firebase/firebase';

const DEFAULT_FILTER_STATE = {
  regionIds: [],
  categoryIds: [],
  dateRangeType: 'ALL', // 'ALL' | 'RANGE'
  dateFrom: '',
  dateTo: '',
  keywords: '',
  infoFilterA: '', // First text field
  infoFilterB: '', // Second text field
  unpublishedBatch: 'ALL', // AMENDED: Replaced showUnpublished with pulldown state defaulting to ALL
};

export const useFilterManage = (initialValues = null) => {
  // --- DATA SOURCES ---
  const [allRegions, setAllRegions] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [excelImports, setExcelImports] = useState([]);
  const [excelArchives, setExcelArchives] = useState([]); // AMENDED: Added state for excelArchives
  const [loading, setLoading] = useState(true);

  // --- FILTER STATE ---
  const [filterState, setFilterState] = useState(initialValues || DEFAULT_FILTER_STATE);

  // --- UI STATE ---
  const [activeSubmenu, setActiveSubmenu] = useState('ACTIVE');
  const [showClearAlert, setShowClearAlert] = useState(false);

  // --- FETCH DATA (Unconditional) ---
  useEffect(() => {
    setLoading(true);

    // Fetch Regions
    const unsubRegions = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAllRegions(data);
      },
      (error) => console.log('Region fetch stopped:', error.code),
    );

    // Fetch Categories
    const unsubCategories = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAllCategories(data);
        setLoading(false);
      },
      (error) => console.log('Category fetch stopped:', error.code),
    );

    // Fetch excelImports
    const unsubExcelImports = onSnapshot(
      collection(db, 'excelImports'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          filename: doc.data().filename || doc.id,
          ...doc.data(),
        }));
        setExcelImports(data);
      },
      (error) => console.log('ExcelImports fetch stopped:', error.code),
    );

    // Fetch excelArchives
    const unsubExcelArchives = onSnapshot(
      collection(db, 'excelArchives'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          filename: doc.data().filename || doc.id,
          ...doc.data(),
        }));
        setExcelArchives(data);
      },
      (error) => console.log('ExcelArchives fetch stopped:', error.code),
    );

    return () => {
      unsubRegions();
      unsubCategories();
      unsubExcelImports();
      unsubExcelArchives();
    };
  }, []);

  // AMENDED: Combine and sort all batches by date descending
  const allBatches = [...excelImports, ...excelArchives].sort((a, b) => {
    const getMs = (item) => {
      const d = item.importDate;
      if (!d) return 0;
      if (typeof d.toMillis === 'function') return d.toMillis();
      if (d.seconds) return d.seconds * 1000;
      return new Date(d).getTime() || 0;
    };
    return getMs(b) - getMs(a);
  });

  // --- ACTIONS ---

  const handleFieldChange = (field, value) => {
    setFilterState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClear = () => {
    setFilterState(DEFAULT_FILTER_STATE);
    setShowClearAlert(false);
  };

  const toggleSelection = (field, id) => {
    setFilterState((prev) => {
      const current = prev[field] || [];
      const updated = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, [field]: updated };
    });
  };

  const toggleAll = (field, allIds, isChecked) => {
    setFilterState((prev) => ({
      ...prev,
      [field]: isChecked ? allIds : [],
    }));
  };

  // --- FETCH UNIQUE KEYWORDS ---
  const fetchUniqueKeywords = async () => {
    try {
      const q = query(collection(db, 'markerGroups'));
      const snapshot = await getDocs(q);
      const uniqueWords = new Set();

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.keywords && typeof data.keywords === 'string') {
          const words = data.keywords.split(/[\s,]+/).filter((w) => w.trim().length > 0);
          words.forEach((w) => uniqueWords.add(w.trim()));
        }
      });

      return Array.from(uniqueWords).sort((a, b) => a.localeCompare(b));
    } catch (error) {
      console.error('Error fetching keywords:', error);
      return [];
    }
  };

  return {
    state: {
      filterState,
      allRegions,
      allCategories,
      allBatches, // AMENDED: Export unified batches to panel
      activeSubmenu,
      loading,
      showClearAlert,
    },
    actions: {
      setActiveSubmenu,
      handleFieldChange,
      handleClear,
      toggleSelection,
      toggleAll,
      setShowClearAlert,
      fetchUniqueKeywords,
    },
  };
};
